import { useEffect, useRef } from "react";
import type { Kommentar } from "./kommentarModell";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

/**
 * DIE LEISTE ERST, WENN DIE MARKIERUNG FERTIG IST (PO 28.09.2026:
 * „Kommentar|Erledigt erst anzeigen wenn final markiert").
 *
 * `selectionchange` feuert bei JEDER Bewegung der Maus, während man zieht.
 * Die Leiste sprang deshalb schon beim ersten Buchstaben auf und lief der
 * Markierung hinterher — mitten über dem Text, den man gerade markieren will.
 *
 * Jetzt:
 *  · Maus gedrückt → Leiste weg, und sie bleibt weg, solange gezogen wird.
 *  · Maus losgelassen → EINMAL prüfen (nach dem Loslassen hat der Browser die
 *    Markierung abgeschlossen; ein Takt später, damit sie sicher steht).
 *  · Tastatur (Umschalt + Pfeil) → erst prüfen, wenn eine kurze Pause kommt;
 *    wer mit gedrückter Umschalttaste Wort für Wort markiert, ist noch nicht
 *    fertig.
 *
 * Als eigener Hook, damit das Notizfeld selbst nur EINE Zeile dafür trägt.
 */
export const TASTATUR_RUHE_MS = 350;

export function useMarkierungFertig(
    feldRef: { current: HTMLElement | null },
    pruefen: () => void,
    verbergen: () => void,
): void {
    const ziehtRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pruefenRef = useRef(pruefen);
    pruefenRef.current = pruefen;
    const verbergenRef = useRef(verbergen);
    verbergenRef.current = verbergen;

    useEffect(() => {
        const spaeter = (ms: number) => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                if (!ziehtRef.current) pruefenRef.current();
            }, ms);
        };
        const runter = (e: MouseEvent) => {
            const feld = feldRef.current;
            if (!feld || !feld.contains(e.target as Node)) return;
            ziehtRef.current = true;
            verbergenRef.current();
        };
        const hoch = () => {
            if (!ziehtRef.current) return;
            ziehtRef.current = false;
            spaeter(0);
        };
        const auswahl = () => {
            // Während gezogen wird: nichts zeigen. Sonst (Tastatur, Doppelklick
            // ist schon mit `mouseup` erledigt) erst nach einer Pause.
            verbergenRef.current();
            if (ziehtRef.current) return;
            spaeter(TASTATUR_RUHE_MS);
        };
        alleDokumente.addEventListener("mousedown", runter, true);
        alleDokumente.addEventListener("mouseup", hoch, true);
        alleDokumente.addEventListener("selectionchange", auswahl);
        return () => {
            alleDokumente.removeEventListener("mousedown", runter, true);
            alleDokumente.removeEventListener("mouseup", hoch, true);
            alleDokumente.removeEventListener("selectionchange", auswahl);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [feldRef]);
}

/**
 * Eine Stelle, deren Kommentare ALLE abgehakt sind, wird grün (Plan
 * 20.09.2026, Paket 5: „Ist alles abgehakt, wird die Stelle grün").
 *
 * Als Stilregel je Anker statt als Klasse im Text: das Feld speichert, was in
 * seinem DOM steht — eine Klasse dort landete in der Datenbank und stimmte
 * nach dem nächsten Haken nicht mehr. Der Anker ist geprüft
 * (`[A-Za-z0-9]{6,24}`, wie im Reiniger), er kann den Selektor nicht sprengen.
 */
export function erledigteStellenCss(faeden: Map<string, Kommentar[]> | undefined): string {
    if (!faeden) return "";
    const anker = Array.from(faeden)
        .filter(([a, ks]) => /^[A-Za-z0-9]{6,24}$/.test(a) && ks.length > 0 && ks.every((k) => !!k.erledigtAm))
        .map(([a]) => a);
    if (!anker.length) return "";
    const stellen = anker.map((a) => `.nf-feld .nb-anmerk[data-anker="${a}"]`);
    return (
        `${stellen.join(",")}{background:#e9f4ee;box-shadow:inset 0 -2px 0 #9cc9b3}` +
        `${stellen.map((s) => `${s}::after`).join(",")}{color:#2f7d5b}`
    );
}
